import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { 
  validateFacturationsBatch, 
  payCommissionsBatch,
  payFinalFacturationsBatch 
} from '../../service/api/facturationService';
import { getFacturationState, handleApiError } from '../../service/utils/facturationUtils';
import useSelectedDomain from '../../service/Hooks/useSelectedDomain';

/**
 * ACTIONS PAR LOT - Gère les opérations groupées
 */
const BatchActions = ({ facturations, onOperationComplete }) => {
  const { t } = useTranslation('facturation');
  const selectedDomain = useSelectedDomain();
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loading, setLoading] = useState(null);

  const getEligibleItems = (operation) => {
    return facturations.filter(f => {
      const state = getFacturationState(f);
      
      switch (operation) {
        case 'validation':
          return state.canValidate && selectedItems.has(f.id);
        case 'commission':
          return state.canPayCommission && selectedItems.has(f.id);
        case 'final':
          return state.canFinalPay && selectedItems.has(f.id);
        default:
          return false;
      }
    });
  };

  const toggleSelectAll = () => {
    if (selectedItems.size === facturations.length) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(facturations.map(f => f.id)));
    }
  };

  const handleBatchValidation = async () => {
    const eligibleItems = getEligibleItems('validation');
    if (eligibleItems.length === 0) {
      toast.warning(t('warnings.noEligible.validation'));
      return;
    }

    setLoading('validation');

    try {
      const facturationIds = eligibleItems.map(item => item.id);
      const result = await validateFacturationsBatch(facturationIds, selectedDomain.id);
      
      handleBatchResult(result, t('workflow.steps.validation'));
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(`${t('errors.batch.validation')}: ${errorMessage}`);
    } finally {
      setLoading(null);
    }
  };

  const handleBatchCommissionPayment = async () => {
    const eligibleItems = getEligibleItems('commission');
    if (eligibleItems.length === 0) {
      toast.warning(t('warnings.noEligible.commission'));
      return;
    }

    setLoading('commission');

    try {
      const facturationIds = eligibleItems.map(item => item.id);
      const result = await payCommissionsBatch(facturationIds, selectedDomain.id);
      
      handleBatchResult(result, t('workflow.steps.commission'));
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(`${t('errors.batch.commission')}: ${errorMessage}`);
    } finally {
      setLoading(null);
    }
  };

  const handleBatchFinalPayment = async () => {
    const eligibleItems = getEligibleItems('final');
    if (eligibleItems.length === 0) {
      toast.warning(t('warnings.noEligible.final'));
      return;
    }

    setLoading('final');

    try {
      const facturationIds = eligibleItems.map(item => item.id);
      const result = await payFinalFacturationsBatch(facturationIds, selectedDomain.id);
      
      handleBatchResult(result, t('workflow.steps.final'));
    } catch (error) {
      const errorMessage = handleApiError(error);
      toast.error(`${t('errors.batch.final')}: ${errorMessage}`);
    } finally {
      setLoading(null);
    }
  };

  const handleBatchResult = (result, operationType) => {
    if (result.success) {
      const { successCount, failureCount } = result.data;
      
      if (successCount > 0) {
        toast.success(`${successCount} ${operationType}(s) ${t('success.batch')}`);
      }
      
      if (failureCount > 0) {
        toast.error(`${failureCount} ${t('errors.batch.failure', { operation: operationType })}`);
      }
      
      setSelectedItems(new Set());
      onOperationComplete?.();
    } else {
      toast.error(result.message || `${t('errors.batch.generic')} ${operationType}`);
    }
  };

  const stats = {
    total: facturations.length,
    canValidate: facturations.filter(f => getFacturationState(f).canValidate).length,
    canPayCommission: facturations.filter(f => getFacturationState(f).canPayCommission).length,
    canFinalPay: facturations.filter(f => getFacturationState(f).canFinalPay).length,
    completed: facturations.filter(f => getFacturationState(f).canViewOnly).length
  };

  const selectedCount = selectedItems.size;

  return (
    <div className="card mb-4 border-0 shadow-sm">
      <div className="card-body">
        {/* En-tête informatif */}
        <div className="row mb-3">
          <div className="col">
            <h6 className="mb-2 fw-bold text-primary">{t('workflow.title')}</h6>
            <div className="d-flex flex-wrap gap-4 small text-muted">
              <span>{t('workflow.stats.total')}: <strong>{stats.total}</strong></span>
              <span>1️. {t('workflow.stats.toValidate')}: <strong>{stats.canValidate}</strong></span>
              <span>2️.  {t('workflow.stats.commission')}: <strong>{stats.canPayCommission}</strong></span>
              <span>3️. {t('workflow.stats.final')}: <strong>{stats.canFinalPay}</strong></span>
              <span>{t('workflow.stats.completed')}: <strong>{stats.completed}</strong></span>
            </div>
          </div>
        </div>

        {/* Barre de contrôle */}
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
          {/* Sélection */}
          <div className="d-flex align-items-center gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                checked={selectedCount === stats.total && stats.total > 0}
                onChange={toggleSelectAll}
                disabled={!!loading}
              />
              <label className="form-check-label fw-semibold">
                {selectedCount > 0 ? 
                  t('workflow.actions.selected', { count: selectedCount }) : 
                  t('workflow.actions.selectAll')}
              </label>
            </div>
          </div>

          {/* Actions par étape */}
          <div className="d-flex gap-2 flex-wrap">
            {/* Étape 1: Validation */}
            <button
              className="btn btn-primary btn-sm"
              onClick={handleBatchValidation}
              disabled={loading || getEligibleItems('validation').length === 0}
            >
              {loading === 'validation' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('workflow.actions.validate.loading')}
                </>
              ) : (
                <>
                  <i className="fas fa-check-circle me-2"></i>
                  {t('workflow.actions.validate.label')} ({getEligibleItems('validation').length})
                </>
              )}
            </button>

            {/* Étape 2: Commission */}
            <button
              className="btn btn-warning btn-sm"
              onClick={handleBatchCommissionPayment}
              disabled={loading || getEligibleItems('commission').length === 0}
            >
              {loading === 'commission' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('workflow.actions.commission.loading')}
                </>
              ) : (
                <>
                  <i className="fas fa-credit-card me-2"></i>
                  {t('workflow.actions.commission.label')} ({getEligibleItems('commission').length})
                </>
              )}
            </button>

            {/* Étape 3: Paiement Final */}
            <button
              className="btn btn-success btn-sm"
              onClick={handleBatchFinalPayment}
              disabled={loading || getEligibleItems('final').length === 0}
            >
              {loading === 'final' ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                  {t('workflow.actions.final.loading')}
                </>
              ) : (
                <>
                  <i className="fas fa-money-bill-wave me-2"></i>
                  {t('workflow.actions.final.label')} ({getEligibleItems('final').length})
                </>
              )}
            </button>

            {/* Réinitialiser */}
            {selectedCount > 0 && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setSelectedItems(new Set())}
                disabled={!!loading}
              >
                <i className="fas fa-times me-2"></i>
                {t('workflow.actions.cancel')}
              </button>
            )}
          </div>
        </div>

        {/* Guide visuel du workflow */}
        {selectedCount > 0 && (
          <div className="mt-3 p-3 bg-light rounded">
            <div className="row text-center">
              <div className="col">
                <div className={`p-2 rounded ${getEligibleItems('validation').length > 0 ? 'bg-primary text-white' : 'bg-light'}`}>
                  <small>{t('workflow.guide.step1')}</small>
                  <br />
                  <strong>{getEligibleItems('validation').length}</strong>
                </div>
              </div>
              <div className="col">
                <div className={`p-2 rounded ${getEligibleItems('commission').length > 0 ? 'bg-warning text-dark' : 'bg-light'}`}>
                  <small>{t('workflow.guide.step2')}</small>
                  <br />
                  <strong>{getEligibleItems('commission').length}</strong>
                </div>
              </div>
              <div className="col">
                <div className={`p-2 rounded ${getEligibleItems('final').length > 0 ? 'bg-success text-white' : 'bg-light'}`}>
                  <small>{t('workflow.guide.step3')}</small>
                  <br />
                  <strong>{getEligibleItems('final').length}</strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BatchActions;